import { type MouseEventHandler } from 'react'
import {
  AI_GENERATION_URL,
  AI_KITCHEN_ART_URL,
  AI_PLACES_URL,
  CDN_URL,
  HOME_AND_HOBBIES_URL,
} from 'config/config'
import { BREAKPOINTS } from 'mixtiles-web-common'
import { type OneLinkConfig } from 'hooks/useOneLinkUrl'

export const MULTI_PRODUCT_VIDEO_FOLDER = 'multi-product-homepage'

const MULTI_PRODUCT_IMAGES_URL = `${CDN_URL}/multi-product-homepage`

export const MULTI_PRODUCT_HOMEPAGE_SOURCE = 'multi-product-homepage'
export const MULTI_PRODUCT_NAV_STATE = { source: MULTI_PRODUCT_HOMEPAGE_SOURCE }

export const MOBILE_VIDEO_ASPECT_RATIO = '393 / 491'

// Figma's desktop hero heights; the video covers the hero, cropping what doesn't fit. Below 1280px,
// 800px would crop the side-by-side videos to about half their width, so design uses the Mobile L frame's height.
export const MEDIUM_SCREEN_HERO_HEIGHT = 596
export const LARGE_SCREEN_HERO_HEIGHT = 800

export const DESKTOP_HERO_ROW_GAP = 8

export type CtaLink = {
  label: string
  to: string
  onClick: MouseEventHandler<HTMLElement>
}

export type HeroCta = CtaLink & { id: string; variant: 'primary' | 'glass' }

export const APP_STORE_URL =
  'https://apps.apple.com/il/app/mixtiles-tiles-photo-books/id583099157'

export const MULTI_PRODUCT_APP_ONELINK: OneLinkConfig = {
  oneLinkUrl: 'https://mixtiles.onelink.me/Codm/HP',
}

// Figma's mockup frame for the app section; the phone image sits `phoneTop` px below its top, and
// the phone's shadow overflows its bottom edge.
export const MULTI_PRODUCT_APP_MOCKUP_FRAME = {
  width: 625,
  height: 627,
  phoneTop: 70,
}

// Phone frame with the app's static UI; `screenWindow` is the transparent area (in image px) where the video plays,
// and `phoneBody` is the phone itself; around it are margin and shadow.
export const MULTI_PRODUCT_APP_PHONE_IMAGE = {
  src: `${MULTI_PRODUCT_IMAGES_URL}/app-phone.webp`,
  srcSet: `${MULTI_PRODUCT_IMAGES_URL}/app-phone-640.webp 640w, ${MULTI_PRODUCT_IMAGES_URL}/app-phone.webp 1247w`,
  sizes: `(min-width: ${BREAKPOINTS.md}px) ${MULTI_PRODUCT_APP_MOCKUP_FRAME.width}px, 320px`,
  width: 1247,
  height: 1177,
  screenWindow: { top: 127, left: 410, width: 431 },
  phoneBody: { left: 381, right: 868 },
}

const MULTI_PRODUCT_VIDEOS_PATH = 'assets/homepage/multi-product-homepage'

export const MULTI_PRODUCT_APP_INTRO_VIDEO = `${MULTI_PRODUCT_VIDEOS_PATH}/app-intro`
export const MULTI_PRODUCT_APP_SCREEN_VIDEO = `${MULTI_PRODUCT_VIDEOS_PATH}/app-screen-v2`

type HeroVideoScreen = 'mobile' | 'desktop'
export type HeroVideo = { cldId: string; cldWidth?: number }
// 'play-once' and 'wait' are for heroes that take turns: one plays through once, then hands over.
export type HeroPlayback = 'loop' | 'play-once' | 'wait'

// Figma's 1920px desktop frame makes each side-by-side hero 956px wide.
const SIDE_BY_SIDE_VIDEO_CLD_WIDTH = 1000
// The Photo Books and Pet Portraits desktop videos are 836px tall at that width.
export const SIDE_BY_SIDE_VIDEO_ASPECT_RATIO = `${SIDE_BY_SIDE_VIDEO_CLD_WIDTH} / 836`

// The Photo Walls desktop video's frame.
export const PHOTO_WALLS_VIDEO_ASPECT_RATIO = '1920 / 842'

export const MULTI_PRODUCT_HERO_VIDEOS = {
  photoWalls: {
    mobile: { cldId: `${MULTI_PRODUCT_VIDEOS_PATH}/tiles-intro` },
    desktop: {
      cldId: `${MULTI_PRODUCT_VIDEOS_PATH}/photo-walls-desktop`,
      // The source width; a wider transform would upscale it.
      cldWidth: 1920,
    },
  },
  photoBooks: {
    mobile: { cldId: `${MULTI_PRODUCT_VIDEOS_PATH}/photo-books-intro` },
    desktop: {
      cldId: `${MULTI_PRODUCT_VIDEOS_PATH}/photo-books-desktop`,
      cldWidth: SIDE_BY_SIDE_VIDEO_CLD_WIDTH,
    },
  },
  petPortraitsIntro: {
    mobile: { cldId: `${MULTI_PRODUCT_VIDEOS_PATH}/pet-portraits-intro` },
    desktop: {
      cldId: `${MULTI_PRODUCT_VIDEOS_PATH}/pet-portraits-desktop-intro`,
      cldWidth: SIDE_BY_SIDE_VIDEO_CLD_WIDTH,
    },
  },
  petPortraitsLoop: {
    mobile: { cldId: `${MULTI_PRODUCT_VIDEOS_PATH}/pet-portraits-loop` },
    desktop: {
      cldId: `${MULTI_PRODUCT_VIDEOS_PATH}/pet-portraits-desktop-loop`,
      cldWidth: SIDE_BY_SIDE_VIDEO_CLD_WIDTH,
    },
  },
} satisfies Record<string, Record<HeroVideoScreen, HeroVideo>>

export const MULTI_PRODUCT_PHOTO_WALLS_PHOTOSHOOT_MOBILE_VIDEO: HeroVideo = {
  cldId: `${MULTI_PRODUCT_VIDEOS_PATH}/tiles-intro-photoshoot`,
}

type MultiProductStat = {
  value: number
  suffix: string
  captionKey: string
  notation: 'standard' | 'compact'
}

export const MULTI_PRODUCT_STATS: MultiProductStat[] = [
  {
    value: 4.9,
    suffix: '/5',
    captionKey: 'multi_product_homepage.stats.rating_caption',
    notation: 'standard',
  },
  {
    value: 4_000_000,
    suffix: '+',
    captionKey: 'multi_product_homepage.stats.customers_caption',
    notation: 'standard',
  },
]

export const DESKTOP_MULTI_PRODUCT_STATS: MultiProductStat[] = [
  ...MULTI_PRODUCT_STATS,
  {
    value: 100_000_000,
    suffix: '+',
    captionKey: 'multi_product_homepage.desktop_stats.photos_printed_caption',
    notation: 'compact',
  },
]

export type CtaProduct =
  | 'Single Tiles'
  | 'Photo Books'
  | 'Pet Portraits'
  | 'App'
  | 'Caricature'
  | 'The Studio'
  | 'Kitchen Art'
  | 'Travel'
  | 'Home & Hobbies'
  | 'Family Portraits'
  | 'Travel Posters'

export type CtaAction =
  | 'Buy'
  | 'Continue Design'
  | 'Learn More'
  | 'Explore'
  | 'Download'

export type NewProductConfig = {
  labelKey: string
  url: string
  alt: CtaProduct
  imageUrl: string
  external?: boolean
}

export const NEW_PRODUCT_ITEMS: NewProductConfig[] = [
  {
    labelKey: 'multi_product_homepage.new_products.caricature',
    url: `${AI_GENERATION_URL}/family?template=caricature-drawing`,
    alt: 'Caricature',
    imageUrl: `${MULTI_PRODUCT_IMAGES_URL}/caricature-400.webp`,
  },
  {
    labelKey: 'multi_product_homepage.new_products.the_studio',
    url: `${AI_GENERATION_URL}/family?template=family_studio`,
    alt: 'The Studio',
    imageUrl: `${MULTI_PRODUCT_IMAGES_URL}/studio-400.webp`,
  },
  {
    labelKey: 'ai_templates.menu_title_kitchen_art',
    url: AI_KITCHEN_ART_URL,
    alt: 'Kitchen Art',
    imageUrl: `${MULTI_PRODUCT_IMAGES_URL}/home-and-hobbies-400.webp`,
    external: true,
  },
  {
    labelKey: 'ai_templates.menu_title_travel',
    url: AI_PLACES_URL,
    alt: 'Travel',
    imageUrl: `${MULTI_PRODUCT_IMAGES_URL}/travel-posters-400.webp`,
    external: true,
  },
]

export const DESKTOP_NEW_PRODUCT_ITEMS: NewProductConfig[] = [
  {
    labelKey: 'ai_templates.menu_title_home_hobbies',
    url: HOME_AND_HOBBIES_URL,
    alt: 'Home & Hobbies',
    imageUrl: `${MULTI_PRODUCT_IMAGES_URL}/home-and-hobbies.webp`,
    external: true,
  },
  {
    labelKey: 'ai_templates.menu_title_family',
    url: `${AI_GENERATION_URL}/family?template=family_studio`,
    alt: 'Family Portraits',
    imageUrl: `${MULTI_PRODUCT_IMAGES_URL}/family-portraits.webp`,
  },
  {
    labelKey: 'ai_templates.menu_title_travel_posters',
    url: AI_PLACES_URL,
    alt: 'Travel Posters',
    imageUrl: `${MULTI_PRODUCT_IMAGES_URL}/travel-posters.webp`,
    external: true,
  },
]
